import { useMemo, useState } from "react";
import type { TypedWrappedFieldProps } from "redux-form";
import _ from "underscore";

import { Box, Stack, Typography } from "@braintrust/braintrust-ui-components";
import { ErrorSVGIcon } from "@braintrust/braintrust-ui-components/Icons";
import { SortableList } from "@js/components/sortable-list/motion";
import { useLatestCallback } from "@js/hooks/use-latest-callback";
import type { InterviewQuestion } from "@js/types/jobs";

import { MAX_QUESTIONS_COUNT } from "../../validator";
import { AddItemTextField } from "../add-item-text-field";

import { InterviewQuestionItem } from "./interview-question-item";

import style from "./style.module.scss";

type JobInterviewQuestionsProps = TypedWrappedFieldProps<
  InterviewQuestion[]
> & { handleQuestionsChange: () => void };

export const JobInterviewQuestions = ({
  input,
  meta,
  handleQuestionsChange,
}: JobInterviewQuestionsProps) => {
  const isErrorInSection = !!meta.error;
  const isAnyTouched = !!meta.dirty || meta.submitFailed;
  const displayError = isErrorInSection && isAnyTouched;
  const generalError = meta.error?._error
    ? String(meta.error?._error)
    : undefined;

  const validationError =
    typeof meta.error === "string" ? meta.error : undefined;

  const sectionError = generalError || validationError;
  const [questionItems, setQuestionItems] = useState<InterviewQuestion[]>(
    input.value || [],
  );

  const updateItems = (items: InterviewQuestion[]) => {
    input.onChange(items);
    setQuestionItems(items);
  };

  const updateItemsCallback = useLatestCallback(updateItems);
  const debounceReorder = useMemo(() => {
    return _.debounce(updateItemsCallback, 100);
  }, [updateItemsCallback]);

  const handleChangeItem = useLatestCallback(
    (id: number, patch: Partial<InterviewQuestion>) => {
      const hasQuestionChanged = typeof patch.question !== "undefined";
      if (hasQuestionChanged) {
        handleQuestionsChange();
      }

      updateItems(
        questionItems.map((question) =>
          question.id !== id ? question : { ...question, ...patch },
        ),
      );
    },
  );

  const handleDeleteItem = useLatestCallback((id: number) => {
    handleQuestionsChange();
    updateItems(
      questionItems
        .filter((question) => question.id !== id)
        .map((item, index) => {
          return { ...item, order: index };
        }),
    );
  });

  const showAddQuestionButton = questionItems.length < MAX_QUESTIONS_COUNT;

  return (
    <Stack
      sx={{
        padding: 4,
        borderRadius: 4,
        backgroundColor: "var(--white)",
        border: displayError
          ? "1px solid var(--negative-2)"
          : "1px solid var(--grey-4)",
      }}
    >
      <Stack direction="row" sx={{ alignItems: "center", mb: 0.5 }}>
        {displayError && (
          <ErrorSVGIcon
            sx={{
              color: "var(--negative-2)",
              fontSize: "20px",
              mr: 1,
            }}
          />
        )}
        <Typography component="h1" size="large" fontWeight={400}>
          Interview Questions
        </Typography>
      </Stack>
      <Typography
        component="span"
        size="small"
        fontWeight={400}
        sx={{ mb: questionItems.length ? 3 : 0 }}
      >
        Questions will be asked in the order in which they are listed here.
      </Typography>
      <SortableList<InterviewQuestion>
        className={style.sortableList}
        items={questionItems}
        onReorder={async (reorderedItems: InterviewQuestion[]) => {
          const itemsWithOrder = reorderedItems.map((item, index) => ({
            ...item,
            order: index,
          }));
          debounceReorder(itemsWithOrder);
        }}
        style={{
          display: "grid",
          gap: "16px",
        }}
      >
        {(item, index, isDragging) => {
          return (
            <InterviewQuestionItem
              index={index}
              item={item}
              isDragging={isDragging}
              onDelete={handleDeleteItem}
              onChange={handleChangeItem}
              error={meta.error?.[index]}
              displayError={displayError}
            />
          );
        }}
      </SortableList>

      {showAddQuestionButton && (
        <Box sx={{ mt: 2, with: "100%" }}>
          <AddItemTextField
            iconButtonAriaLabel="Add question"
            id="add-question"
            placeholder="Add question"
            onAdd={(question: string) => {
              const newItemId = !!questionItems.length
                ? Math.max(...(questionItems || []).map((item) => item.id)) + 1
                : 0;
              const newItemOrder = questionItems.length;
              handleQuestionsChange();
              updateItems([
                ...questionItems,
                getEmptyInterviewQuestionItem({
                  question,
                  newItemId,
                  order: newItemOrder,
                }),
              ]);
            }}
          />
        </Box>
      )}

      {displayError && !!sectionError && (
        <Box
          sx={{
            background: "#F7626240",
            p: "8px 16px",
            borderRadius: 2,
            mt: 2,
          }}
        >
          <Typography component="p" size="small">
            {sectionError}
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

const getEmptyInterviewQuestionItem = ({
  newItemId,
  order,
  question,
}: {
  question: string;
  newItemId: number;
  order: number;
}): InterviewQuestion => {
  return {
    id: newItemId,
    question,
    order,
    follow_up_strategy: ENUMS.JobInterviewQuestionsFollowUpStrategy.NATURAL,
  };
};
